import { Link } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { Button } from '@reflex/ui/components/ui/button';
import { useDocumentTitle } from '@reflex/ui/hooks/useDocumentTitle';
import { useOrgRoutes } from '@/lib/useOrgRoutes';

export interface AgentNotFoundStateProps {
  agentId: string;
}

/**
 * Shown in place of the agent stream when the server answers the agent read
 * with a 404: no run with that id exists in the active organization. The
 * usual way to get here is an org switch from a run's URL — the id belongs
 * to the org that was left, so it names nothing here.
 *
 * This is a dead end, stated as one. Rendering the launch placeholder
 * instead (what this page used to fall through to) reads as "this org has
 * no runs", so a broken deep link looks like an empty workspace and any
 * bug report filed from it is unreproducible.
 *
 * It is also where a *remote* delete of the open run lands, on purpose.
 * Deleting a run you are watching yourself navigates away explicitly
 * (`useStopViewingDeletedAgents`), because you know what you just did. When
 * someone else deletes it, the `agent:deleted` broadcast evicts the cache,
 * the live observer refetches, and the server answers 404 — so you stop on
 * this screen, which names the run and says it may have been deleted. The
 * behaviour this replaced silently redirected to the run list and auto-opened
 * whichever run was next, i.e. swapped the transcript under the reader with
 * no explanation. Being told is worth the extra click.
 */
export function AgentNotFoundState({ agentId }: AgentNotFoundStateProps) {
  const routes = useOrgRoutes();
  useDocumentTitle('Session not found');

  return (
    <EmptyState
      variant="fullHeight"
      icon={<SearchX className="icon-md text-muted-foreground" />}
      title="Session not found"
      description={`No session with the id ${agentId} exists in this organization. It may belong to another organization, or it may have been deleted.`}
      action={
        <Button asChild>
          <Link to={routes.runs}>View sessions</Link>
        </Button>
      }
    />
  );
}
